import { useEffect, useMemo } from 'react';
import { create } from 'zustand';
import { ClipboardList, X } from 'lucide-react';
import type { CustomField, CustomFieldValueSet } from '@/lib/api/types';
import { useBoard, useBulkAction, useFieldPolicies, useFields } from '@/lib/api/queries';
import { useUploadStore, type UploadRecord } from '@/stores/upload';
import { useSessionStore } from '@/stores/session';
import { Button, IconButton } from '@/components/Button';
import { toast } from '@/stores/ui';
import { isApiError } from '@/lib/api/errors';
import { hasPermission } from '@/lib/shared';
import { FieldValueInput } from '@/features/field-policies/FieldValueInput';
import { isEmptyValue } from '@/features/field-policies/policy';

/** Uploads whose asset exists server-side (the version is processing or ready), so values can be written now. */
const WRITABLE: ReadonlySet<UploadRecord['status']> = new Set(['processing', 'ready']);
const DONE: ReadonlySet<UploadRecord['status']> = new Set(['failed', 'cancelled']);

/** `written` / `inflight` / `failed` hold client upload ids: confirmed by the server, being sent, rejected (retry clears it). */
type BoardStep = { values: Record<string, CustomFieldValueSet | null>; applied: Record<string, CustomFieldValueSet> | null; written: string[]; inflight: string[]; failed: string[]; dismissed: boolean; open: boolean };
type StepStore = { boards: Record<string, BoardStep>; patch: (boardId: string, p: Partial<BoardStep>) => void; reset: () => void };

const EMPTY: BoardStep = { values: {}, applied: null, written: [], inflight: [], failed: [], dismissed: false, open: false };

/** Lives outside the tray so collapsing or re-docking the panel keeps the answers; cleared with the tray. */
export const useCompleteMetadataStore = create<StepStore>()((set) => ({
  boards: {},
  patch: (boardId, p) => set((s) => ({ boards: { ...s.boards, [boardId]: { ...EMPTY, ...s.boards[boardId], ...p } } })),
  reset: () => set({ boards: {} }),
}));

/** New-asset uploads of members (not guests, not new versions) grouped by target board. */
export function uploadsByBoard(records: Record<string, UploadRecord>, order: readonly string[]): Map<string, UploadRecord[]> {
  const out = new Map<string, UploadRecord[]>();
  for (const id of order) {
    const r = records[id];
    if (!r || r.guest_jwt || r.is_new_version || DONE.has(r.status)) continue;
    const list = out.get(r.target_board_id) ?? [];
    list.push(r);
    out.set(r.target_board_id, list);
  }
  return out;
}

/** Required fields the board's effective policy leaves to the uploader (no default fills them, not hidden). */
export function fieldsToComplete(fields: readonly CustomField[], effective: ReadonlyArray<{ field_id: string; required?: boolean; hidden?: boolean; default_value?: CustomFieldValueSet | null }>): CustomField[] {
  const byId = new Map(effective.map((e) => [e.field_id, e]));
  return fields.filter((f) => {
    if (f.applies_to !== 'asset') return false;
    const e = byId.get(f.id);
    const required = e ? !!e.required : f.required;
    return required && !e?.hidden && isEmptyValue(e?.default_value ?? null);
  });
}

/**
 * Release 2 G3 upload step: when the target board requires fields that no default fills, the tray asks for them once
 * per board and writes the answers to every upload of the batch as soon as its asset exists (`bulkAssetAction
 * set_custom_field`), so the cards never show the incomplete corner for long. The upload contract is unchanged.
 */
export function CompleteMetadataStep() {
  const order = useUploadStore((s) => s.order);
  const records = useUploadStore((s) => s.records);
  const mask = useSessionStore((s) => s.principal.mask);
  const reset = useCompleteMetadataStore((s) => s.reset);
  const groups = useMemo(() => uploadsByBoard(records, order), [records, order]);
  useEffect(() => {
    if (order.length === 0) reset();
  }, [order.length, reset]);
  if (!hasPermission(mask, 'EDIT_METADATA') || groups.size === 0) return null;
  return (
    <>
      {[...groups].map(([boardId, recs]) => (
        <BoardMetadataStep key={boardId} boardId={boardId} records={recs} />
      ))}
    </>
  );
}

function BoardMetadataStep({ boardId, records }: { boardId: string; records: UploadRecord[] }) {
  const wsId = records[0]!.workspace_id;
  const fields = useFields(wsId);
  const policies = useFieldPolicies(boardId);
  const board = useBoard(boardId);
  const bulk = useBulkAction(wsId);
  const step = useCompleteMetadataStore((s) => s.boards[boardId] ?? EMPTY);
  const patch = useCompleteMetadataStore((s) => s.patch);
  const todo = useMemo(() => fieldsToComplete(fields.data ?? [], policies.data?.effective ?? []), [fields.data, policies.data]);
  const pending = records.filter((r) => r.asset_id && WRITABLE.has(r.status) && ![...step.written, ...step.inflight, ...step.failed].includes(r.client_upload_id));

  // write the applied answers to each upload once its asset exists; an upload counts as written only when every
  // field's bulk call reports its asset as succeeded, so a failed request (or a per-asset failure) can be retried
  const pendingKey = pending.map((r) => r.client_upload_id).join(',');
  useEffect(() => {
    if (!step.applied || !pending.length) return;
    const batch = pending.map((r) => ({ upload: r.client_upload_id, asset: r.asset_id! }));
    const values = step.applied;
    patch(boardId, { inflight: [...step.inflight, ...batch.map((b) => b.upload)] });
    void (async () => {
      let ok = new Set(batch.map((b) => b.asset));
      let error: string | null = null;
      try {
        for (const [field_id, value] of Object.entries(values)) {
          if (!ok.size) break;
          const r = await bulk.mutateAsync({ asset_ids: [...ok], action: 'set_custom_field', params: { field_id, value } });
          const succeeded = new Set(r?.succeeded ?? []);
          error ??= r?.failed?.[0] ? (r.failed[0].message ?? r.failed[0].code) : null;
          ok = new Set([...ok].filter((a) => succeeded.has(a)));
        }
      } catch (e) {
        ok = new Set();
        error = isApiError(e) ? e.message : 'Could not save the metadata';
      }
      const cur = useCompleteMetadataStore.getState().boards[boardId];
      if (!cur) return; // the tray was cleared meanwhile
      const sent = batch.map((b) => b.upload);
      const done = batch.filter((b) => ok.has(b.asset)).map((b) => b.upload);
      const bad = sent.filter((u) => !done.includes(u));
      patch(boardId, { inflight: cur.inflight.filter((u) => !sent.includes(u)), written: [...cur.written, ...done], failed: [...cur.failed, ...bad] });
      if (bad.length) toast({ kind: 'error', title: 'Could not save the metadata', body: error ?? undefined });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by the uploads that became writable
  }, [step.applied, pendingKey]);

  if (!todo.length || step.dismissed) return null;
  const all = records.length;
  const written = records.filter((r) => step.written.includes(r.client_upload_id)).length;
  const failed = records.filter((r) => step.failed.includes(r.client_upload_id)).length;
  const missing = todo.filter((f) => isEmptyValue(step.values[f.id] ?? null));
  const apply = () => {
    const values: Record<string, CustomFieldValueSet> = {};
    for (const f of todo) if (!isEmptyValue(step.values[f.id] ?? null)) values[f.id] = step.values[f.id]!;
    patch(boardId, { applied: values, open: false });
  };
  const name = board.data?.name ?? 'this board';
  return (
    <div className="border-b border-line bg-warn/10 px-3 py-2 text-xs" data-complete-metadata={boardId}>
      <div className="flex items-center gap-2">
        <ClipboardList size={14} className="shrink-0 text-warn" />
        <span className="min-w-0 flex-1">
          {step.applied ? (
            failed ? (
              <>
                Metadata saved for {written} of {all} file{all === 1 ? '' : 's'} in {name} — {failed} could not be saved
              </>
            ) : (
              <>Metadata saved for {written} of {all} file{all === 1 ? '' : 's'} in {name}{written < all ? ' — the rest when they finish' : ''}</>
            )
          ) : (
            <>
              {name} requires {todo.map((f) => f.name).join(', ')} for {all} file{all === 1 ? '' : 's'}
            </>
          )}
        </span>
        {!step.applied && !step.open && (
          <Button size="sm" onClick={() => patch(boardId, { open: true })}>
            Complete metadata
          </Button>
        )}
        {failed > 0 && (
          <Button size="sm" onClick={() => patch(boardId, { failed: [] })}>
            Retry
          </Button>
        )}
        <IconButton label="Dismiss" size="sm" onClick={() => patch(boardId, { dismissed: true })}>
          <X size={13} />
        </IconButton>
      </div>
      {step.open && !step.applied && (
        <form
          className="mt-2 space-y-1.5"
          onSubmit={(e) => {
            e.preventDefault();
            apply();
          }}
        >
          {todo.map((f) => (
            <div key={f.id} className="grid grid-cols-[minmax(0,35%)_1fr] items-center gap-2">
              <span className="truncate text-fg-muted">
                {f.name}
                <span className="text-danger"> *</span>
              </span>
              <FieldValueInput field={f} value={step.values[f.id] ?? null} onChange={(v) => patch(boardId, { values: { ...step.values, [f.id]: v } })} />
            </div>
          ))}
          <div className="flex items-center justify-end gap-2 pt-1">
            {missing.length > 0 && <span className="text-fg-subtle">Left empty: {missing.map((f) => f.name).join(', ')}</span>}
            <Button size="sm" variant="primary" type="submit" disabled={missing.length === todo.length}>
              Apply to {all} file{all === 1 ? '' : 's'}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
